import { useEffect } from "react";

import { useConsent } from "~/context/ConsentContext";

/**
 * Third-party scripts, mounted only after the visitor opts in.
 *
 * Sentry's browser SDK receives the visitor's IP address, so under §25(1)
 * TDDDG and Art. 6(1)(a) GDPR it may not load before consent. Nothing here runs
 * on the server, and nothing runs on first client render.
 *
 * Note on withdrawal: the SDK cannot be fully unloaded once booted, so
 * `rejectAll` after accepting triggers a reload (see ConsentContext).
 */
function SentryClient() {
  useEffect(() => {
    let cancelled = false;

    // Dynamic import: the SDK is not in the initial bundle for visitors who
    // never consent.
    void import("@sentry/remix").then((Sentry) => {
      if (cancelled) return;
      if (Sentry.getClient()) return; // already initialised

      const dsn = window.ENV?.SENTRY_DSN;
      if (!dsn) return;

      Sentry.init({
        dsn,
        // No session replay, no profiling, no performance traces: each captures
        // far more personal data than crash reporting needs.
        tracesSampleRate: 0,
        // Defaults to false; stated so nobody flips it by accident. The consent
        // copy promises IP address and browser details only.
        sendDefaultPii: false,
      });
    });

    return () => {
      cancelled = true;
    };
  }, []);

  return null;
}

export default function GatedScripts() {
  const { consent, isHydrated } = useConsent();

  if (!isHydrated) return null;

  return <>{consent.analytics ? <SentryClient /> : null}</>;
}
